import {
  EncounterCarePlanResponseDto,
  EncounterCarePlanType,
} from "./care-plan.model";

/** Standard clinic care plan template type (API / persisted `templateType`). */
export const CARE_PLAN_TEMPLATE_TYPE = "Care Plan";

/** User-facing label for {@link CARE_PLAN_TEMPLATE_TYPE} (API value unchanged). */
export const CARE_PLAN_DISPLAY_NAME = "Note";

/** Sidebar / encounter panel heading for the care-plan checklist tab. */
export const CARE_PLAN_PANEL_DISPLAY_NAME = "Plan";

/** Persisted template type for the former Orders templates. */
export const ORDER_TEMPLATE_TYPE = "Order";

/** User-facing label for {@link ORDER_TEMPLATE_TYPE} (API value unchanged). */
export const ORDER_TEMPLATE_DISPLAY_NAME = "Protocols";

/** Persisted template type for the new encounter Orders templates. */
export const CLINICAL_ORDER_TEMPLATE_TYPE = "Clinical Order";

/** Org-level diagnostics panel catalog (synthetic listing row, not created via Add Template). */
export const PANELS_TEMPLATE_TYPE = "Panels";

/** User-facing label for {@link CLINICAL_ORDER_TEMPLATE_TYPE}. */
export const CLINICAL_ORDER_DISPLAY_NAME = "Orders";

/** User-facing label for persisted `templateType` values. */
export function getTemplateTypeDisplayName(
  templateType: string | null | undefined
): string {
  const type = String(templateType ?? "").trim();
  switch (type) {
    case CARE_PLAN_TEMPLATE_TYPE:
      return CARE_PLAN_DISPLAY_NAME;
    case PROCEDURE_CARE_PLAN_TEMPLATE_TYPE:
      return "Procedure";
    case ORDER_TEMPLATE_TYPE:
      return ORDER_TEMPLATE_DISPLAY_NAME;
    case CLINICAL_ORDER_TEMPLATE_TYPE:
      return CLINICAL_ORDER_DISPLAY_NAME;
    case PANELS_TEMPLATE_TYPE:
      return "Panels";
    default:
      return type;
  }
}

/** Title for template list badges (uppercase). */
export function getTemplateTypeBadgeLabel(
  templateType: string | null | undefined
): string {
  return getTemplateTypeDisplayName(templateType).toUpperCase();
}

/** Resolve user-facing label from template title or `templateType`. */
export function getTemplateDisplayLabel(
  template:
    | { title?: string | null; templateType?: string | null }
    | null
    | undefined
): string {
  const title = String(template?.title ?? "").trim();
  if (title) {
    return title;
  }
  const displayName = getTemplateTypeDisplayName(template?.templateType);
  return displayName || CARE_PLAN_DISPLAY_NAME;
}

/**
 * Replace Care Plan phrasing in user-facing copy.
 * Procedure Care Plan is handled first so it becomes "Procedure", not "Procedure Note".
 */
export function formatCarePlanDisplayText(text: string): string {
  return text
    .replace(/Procedure Care Plan/g, "Procedure")
    .replace(/procedure care plan/g, "procedure")
    .replace(/Care Plan/g, CARE_PLAN_DISPLAY_NAME)
    .replace(/care plan/g, "note")
    .replace(/CARE PLAN/g, CARE_PLAN_DISPLAY_NAME.toUpperCase());
}

/** Procedure-focused care plan template type — same app behavior as {@link CARE_PLAN_TEMPLATE_TYPE}. */
export const PROCEDURE_CARE_PLAN_TEMPLATE_TYPE = "Procedure Care Plan";

/** Duration dropdown options for Procedure Care Plan templates (minutes), persisted on the template. */
export const PROCEDURE_CARE_PLAN_DURATION_OPTIONS: ReadonlyArray<{
  displayName: string;
  value: string;
}> = [
  { displayName: "10 min", value: "10" },
  { displayName: "15 min", value: "15" },
  { displayName: "20 min", value: "20" },
  { displayName: "25 min", value: "25" },
  { displayName: "30 min", value: "30" },
  { displayName: "45 min", value: "45" },
  { displayName: "60 min", value: "60" },
  { displayName: "90 min", value: "90" },
  { displayName: "120 min", value: "120" },
];

/** Optional section on Procedure Care Plan templates — holds linked Note templates. */
export const PROCEDURE_CARE_PLAN_ENCOUNTER_NOTE_SECTION = "Encounter Note";

/** Procedure-room workflow phases (excludes {@link PROCEDURE_CARE_PLAN_ENCOUNTER_NOTE_SECTION}). */
export const PROCEDURE_CARE_PLAN_WORKFLOW_SECTIONS = [
  "Pre Procedure",
  "Procedure",
  "Post Procedure",
  "Discharge",
] as const;

/** Section buckets when editing a Procedure Care Plan template (includes Encounter Note link). */
export const PROCEDURE_CARE_PLAN_SECTIONS = [
  PROCEDURE_CARE_PLAN_ENCOUNTER_NOTE_SECTION,
  ...PROCEDURE_CARE_PLAN_WORKFLOW_SECTIONS,
] as const;

/** Checklist / encounter UI sections — workflow phases only (not Encounter Note). */
export const PROCEDURE_CARE_PLAN_RUNTIME_UI_SECTIONS =
  PROCEDURE_CARE_PLAN_WORKFLOW_SECTIONS;

function procedureCarePlanSectionKey(
  category: string | null | undefined
): string {
  return String(category ?? "")
    .trim()
    .toLowerCase()
    .replace(/\s+/g, " ");
}

/** True if `category` is the optional Encounter Note section on procedure templates. */
export function isEncounterNoteSectionCategory(
  category: string | null | undefined
): boolean {
  return (
    procedureCarePlanSectionKey(category) ===
    procedureCarePlanSectionKey(PROCEDURE_CARE_PLAN_ENCOUNTER_NOTE_SECTION)
  );
}

/**
 * Encounter Note rows configure the linked `type=note` care plan in the template editor only.
 * They must not appear in runtime checklists or procedure-room UI.
 */
export function shouldHideEncounterNoteSectionInRuntimeUi(
  category: string | null | undefined
): boolean {
  return isEncounterNoteSectionCategory(category);
}

/** True if `category` is a procedure-room workflow phase (not Encounter Note). */
export function isProcedureCarePlanWorkflowSectionCategory(
  category: string | null | undefined
): boolean {
  const key = procedureCarePlanSectionKey(category);
  if (!key) {
    return false;
  }
  return (PROCEDURE_CARE_PLAN_WORKFLOW_SECTIONS as readonly string[]).some(
    (section) => procedureCarePlanSectionKey(section) === key
  );
}

/** True if `category` is one of {@link PROCEDURE_CARE_PLAN_SECTIONS}. */
export function isProcedureCarePlanSectionCategory(
  category: string | null | undefined
): boolean {
  const key = procedureCarePlanSectionKey(category);
  if (!key) {
    return false;
  }
  return (PROCEDURE_CARE_PLAN_SECTIONS as readonly string[]).some(
    (section) => procedureCarePlanSectionKey(section) === key
  );
}

/** Map persisted/API section labels (e.g. `PRE PROCEDURE`) to canonical section names. */
export function canonicalizeProcedureCarePlanSectionCategory(
  category: string | null | undefined
): string {
  const key = procedureCarePlanSectionKey(category);
  if (!key) {
    return "";
  }
  const match = (PROCEDURE_CARE_PLAN_SECTIONS as readonly string[]).find(
    (section) => procedureCarePlanSectionKey(section) === key
  );
  return match ?? String(category ?? "").trim();
}

const PROCEDURE_ROOM_CARE_PLAN_TEMPLATE_ID_PREFIX = "proc-room-";

/**
 * Care-plan checklist rows that belong on the encounter note workflow (Generate / SOAP),
 * not procedure-room phases (Pre/Procedure/Post/Discharge panels).
 */
export function isEncounterScopeCarePlanDetail(detail: {
  category?: string | null;
  templateId?: string | null;
} | null | undefined): boolean {
  if (!detail) {
    return false;
  }
  const category = String(detail.category ?? "").trim();
  if (isProcedureCarePlanWorkflowSectionCategory(category)) {
    return false;
  }
  const templateId = String(detail.templateId ?? "").trim().toLowerCase();
  if (templateId.startsWith(PROCEDURE_ROOM_CARE_PLAN_TEMPLATE_ID_PREFIX)) {
    return false;
  }
  return true;
}

const CARE_PLAN_FAMILY = new Set<string>([
  CARE_PLAN_TEMPLATE_TYPE,
  PROCEDURE_CARE_PLAN_TEMPLATE_TYPE,
]);

/** True when `templateType` is any care-plan family type (standard or procedure). */
export function isCarePlanFamilyTemplateType(
  templateType: string | undefined | null
): boolean {
  if (!templateType) {
    return false;
  }
  return CARE_PLAN_FAMILY.has(String(templateType).trim());
}

/** Care-plan-style container templates attachable on encounters. */
export function isCarePlanContainerTemplate(
  template: { templateType?: string | null } | null | undefined
): boolean {
  return isCarePlanFamilyTemplateType(template?.templateType);
}

/** Standard clinic Note template (`templateType` = Care Plan). */
export function isNoteCarePlanTemplate(
  template: { templateType?: string | null } | null | undefined
): boolean {
  return String(template?.templateType ?? "").trim() === CARE_PLAN_TEMPLATE_TYPE;
}

/** Procedure-room care plan template type. */
export function isProcedureCarePlanTemplateType(
  templateType: string | null | undefined
): boolean {
  return String(templateType ?? "").trim() === PROCEDURE_CARE_PLAN_TEMPLATE_TYPE;
}

/** Normalize API `type` on encounter care plans. */
export function normalizeEncounterCarePlanType(
  value: unknown
): EncounterCarePlanType | null {
  const type = String(value ?? "").trim().toLowerCase();
  if (type === EncounterCarePlanType.NOTE) {
    return EncounterCarePlanType.NOTE;
  }
  if (type === EncounterCarePlanType.PROCEDURE) {
    return EncounterCarePlanType.PROCEDURE;
  }
  return null;
}

/** Map attachable care-plan template to encounter care plan API `type`. */
export function resolveEncounterCarePlanTypeFromTemplate(
  template: { templateType?: string | null } | null | undefined
): EncounterCarePlanType {
  return isProcedureCarePlanTemplateType(template?.templateType)
    ? EncounterCarePlanType.PROCEDURE
    : EncounterCarePlanType.NOTE;
}

/**
 * Prefer templateType, but treat plans with procedure-room workflow categories as
 * `procedure` even when the root template is mislabeled as "Care Plan".
 */
export function resolveEncounterCarePlanTypeFromTemplateOrDetails(
  template: { templateType?: string | null } | null | undefined,
  details?: Array<{ category?: string | null }> | null
): EncounterCarePlanType {
  if (isProcedureCarePlanTemplateType(template?.templateType)) {
    return EncounterCarePlanType.PROCEDURE;
  }
  const hasProcedureWorkflow = (details ?? []).some((detail) =>
    isProcedureCarePlanWorkflowSectionCategory(detail.category)
  );
  if (hasProcedureWorkflow) {
    return EncounterCarePlanType.PROCEDURE;
  }
  return EncounterCarePlanType.NOTE;
}

/**
 * Clinic/Telehealth attaches note care plans only; procedure visits may infer
 * `procedure` from workflow rows on mislabeled templates.
 */
export function resolveEncounterCarePlanTypeForEncounterAttach(
  template: { templateType?: string | null } | null | undefined,
  details: Array<{ category?: string | null }> | null | undefined,
  isProcedureVisit: boolean
): EncounterCarePlanType {
  if (!isProcedureVisit) {
    return resolveEncounterCarePlanTypeFromTemplate(template);
  }
  return resolveEncounterCarePlanTypeFromTemplateOrDetails(template, details);
}

export function isEncounterCarePlanAlreadyExistsError(
  error: unknown
): boolean {
  const e = error as {
    status?: number;
    error?: { statusCode?: number; message?: string };
  };
  return (
    e?.status === 409 ||
    (e?.error?.statusCode === 409 &&
      String(e?.error?.message || "").includes("already exists"))
  );
}

export function findEncounterCarePlanByType(
  carePlans: EncounterCarePlanResponseDto[] | null | undefined,
  type: EncounterCarePlanType
): EncounterCarePlanResponseDto | null {
  return (
    (carePlans ?? []).find(
      (plan) => normalizeEncounterCarePlanType(plan.type) === type
    ) ?? null
  );
}

export function isNoteEncounterCarePlan(
  carePlan: { type?: string | null } | null | undefined
): boolean {
  return (
    normalizeEncounterCarePlanType(carePlan?.type) === EncounterCarePlanType.NOTE
  );
}

/** Note care plan row with no linked template (empty shell). */
export function isUnsetNoteEncounterCarePlan(
  carePlan: { type?: string | null; templateId?: string | null } | null | undefined
): boolean {
  return (
    isNoteEncounterCarePlan(carePlan) &&
    !String(carePlan?.templateId ?? "").trim()
  );
}

/**
 * Procedure care plan that has a template but no detail rows
 * (failed attach / empty shell — needs reassignment).
 */
export function isEmptyProcedureEncounterCarePlan(
  carePlan:
    | {
        type?: string | null;
        templateId?: string | null;
        details?: unknown[] | null;
      }
    | null
    | undefined
): boolean {
  return (
    isProcedureEncounterCarePlanByType(carePlan) &&
    !!String(carePlan?.templateId ?? "").trim() &&
    (carePlan?.details?.length ?? 0) === 0
  );
}

/**
 * Procedure care plan is absent or has no procedure-room workflow rows
 * (Pre/Procedure/Post/Discharge panels will not render).
 */
export function isMissingOrIncompleteProcedureEncounterCarePlan(
  carePlan:
    | {
        id?: string | null;
        type?: string | null;
        details?: Array<{ category?: string | null }> | null;
      }
    | null
    | undefined
): boolean {
  if (!String(carePlan?.id ?? "").trim()) {
    return true;
  }
  const workflowDetails = (carePlan?.details ?? []).filter((detail) =>
    isProcedureCarePlanWorkflowSectionCategory(detail.category)
  );
  return workflowDetails.length === 0;
}

export function isProcedureEncounterCarePlanByType(
  carePlan: { type?: string | null } | null | undefined
): boolean {
  return (
    normalizeEncounterCarePlanType(carePlan?.type) ===
    EncounterCarePlanType.PROCEDURE
  );
}

/**
 * Encounter care plan rooted on a Procedure template, API `type=procedure`,
 * or legacy rows with procedure-room workflow categories.
 */
export function isProcedureEncounterCarePlan(
  carePlan:
    | {
        type?: string | null;
        details?: Array<{ category?: string | null }> | null;
      }
    | null
    | undefined,
  rootTemplate?: { templateType?: string | null } | null
): boolean {
  const normalizedType = normalizeEncounterCarePlanType(carePlan?.type);
  if (normalizedType === EncounterCarePlanType.PROCEDURE) {
    return true;
  }
  if (normalizedType === EncounterCarePlanType.NOTE) {
    return false;
  }
  if (isProcedureCarePlanTemplateType(rootTemplate?.templateType)) {
    return true;
  }
  return (carePlan?.details ?? []).some((detail) =>
    isProcedureCarePlanWorkflowSectionCategory(detail.category)
  );
}
